import React, { useState, useMemo, useCallback } from 'react';
import Button from '@salesforce/design-system-react/components/button';
import Icon from '@salesforce/design-system-react/components/icon';
import Avatar from '@salesforce/design-system-react/components/avatar';
import Input from '@salesforce/design-system-react/components/input';
import InputIcon from '@salesforce/design-system-react/components/icon/input-icon';
import Combobox from '@salesforce/design-system-react/components/combobox';
import Modal from '@salesforce/design-system-react/components/modal';
import Pill from '@salesforce/design-system-react/components/pill';
import Toast from '@salesforce/design-system-react/components/toast';
import ToastContainer from '@salesforce/design-system-react/components/toast/container';
import PageHeader from '@salesforce/design-system-react/components/page-header';

// ============================================================
// Constants & Config
// ============================================================

const GROUP_COLORS = {
  'current-sprint': '#0176d3',
  'up-next': '#9050e9',
  backlog: '#706e6b',
  completed: '#2e844a',
};

const STATUS_CONFIG = {
  'Not Started': { bg: '#e1e5ee', color: '#3e3e3c', label: 'Not Started' },
  'In Progress': { bg: '#45c65a', color: '#fff', label: 'In Progress' },
  'Ready for Review': { bg: '#ffb75d', color: '#3e3e3c', label: 'Ready for Review' },
  Done: { bg: '#00a86b', color: '#fff', label: 'Done' },
  Stuck: { bg: '#e44258', color: '#fff', label: 'Stuck' },
  Blocked: { bg: '#c23934', color: '#fff', label: 'Blocked' },
  'Up Next': { bg: '#a25ddc', color: '#fff', label: 'Up Next' },
  'Waiting for Info': { bg: '#579bfc', color: '#fff', label: 'Waiting for Info' },
};

const PRIORITY_CONFIG = {
  Critical: { bg: '#e44258', color: '#fff', icon: true },
  High: { bg: '#fdac3d', color: '#fff', icon: false },
  Medium: { bg: '#579bfc', color: '#fff', icon: false },
  Low: { bg: '#9aadbd', color: '#fff', icon: false },
};

const SIZE_CONFIG = {
  XS: { label: 'XS', bg: '#e1f0fd', color: '#0176d3' },
  S: { label: 'S', bg: '#d4f0e1', color: '#2e844a' },
  M: { label: 'M', bg: '#fef0e0', color: '#c05c10' },
  L: { label: 'L', bg: '#fce4e2', color: '#c23934' },
  XL: { label: 'XL', bg: '#ede0f9', color: '#9050e9' },
};

const TEAM_MEMBERS = [
  { id: 1, name: 'Sarah Chen', initials: 'SC' },
  { id: 2, name: 'Marcus Johnson', initials: 'MJ' },
  { id: 3, name: 'Priya Patel', initials: 'PP' },
  { id: 4, name: 'Alex Rivera', initials: 'AR' },
  { id: 5, name: 'Jordan Kim', initials: 'JK' },
  { id: 6, name: 'Taylor Brooks', initials: 'TB' },
];

const TAG_OPTIONS = [
  '#api', '#frontend', '#backend', '#ux', '#database', '#security',
  '#performance', '#testing', '#documentation', '#infrastructure',
];

// ============================================================
// Sample Data
// ============================================================

const generateTaskData = () => [
  {
    id: 'current-sprint',
    name: 'Current Sprint',
    collapsed: false,
    tasks: [
      {
        id: 't1', name: 'Implement OAuth 2.0 SSO integration', assignee: TEAM_MEMBERS[0],
        status: 'In Progress', priority: 'Critical', size: 'L', dueDate: '2026-02-20',
        tags: ['#security', '#api'], subtasks: [
          { id: 't1-s1', name: 'Configure identity provider settings', assignee: TEAM_MEMBERS[0], status: 'Done', priority: 'High', size: 'S', dueDate: '2026-02-18', tags: ['#security'] },
          { id: 't1-s2', name: 'Build callback handler endpoint', assignee: TEAM_MEMBERS[3], status: 'In Progress', priority: 'High', size: 'M', dueDate: '2026-02-19', tags: ['#api'] },
        ],
      },
      {
        id: 't2', name: 'Fix pagination bug on Account list view', assignee: TEAM_MEMBERS[1],
        status: 'Ready for Review', priority: 'High', size: 'S', dueDate: '2026-02-19',
        tags: ['#frontend'], subtasks: [],
      },
      {
        id: 't3', name: 'Update donor report export to include YTD totals', assignee: TEAM_MEMBERS[2],
        status: 'In Progress', priority: 'High', size: 'M', dueDate: '2026-02-21',
        tags: ['#backend', '#database'], subtasks: [],
      },
      {
        id: 't4', name: 'Resolve SOQL query governor limit in batch job', assignee: TEAM_MEMBERS[0],
        status: 'Stuck', priority: 'Critical', size: 'M', dueDate: '2026-02-18',
        tags: ['#performance', '#backend'], subtasks: [],
      },
      {
        id: 't5', name: 'Add field-level security for Donation Amount', assignee: TEAM_MEMBERS[3],
        status: 'Done', priority: 'Medium', size: 'S', dueDate: '2026-02-17',
        tags: ['#security'], subtasks: [],
      },
      {
        id: 't6', name: 'Design mobile-responsive layout for Campaign page', assignee: TEAM_MEMBERS[4],
        status: 'Waiting for Info', priority: 'Medium', size: 'L', dueDate: '2026-02-22',
        tags: ['#ux', '#frontend'], subtasks: [],
      },
      {
        id: 't7', name: 'Write Apex unit tests for PaymentProcessor class', assignee: TEAM_MEMBERS[5],
        status: 'In Progress', priority: 'High', size: 'M', dueDate: '2026-02-20',
        tags: ['#testing', '#backend'], subtasks: [],
      },
    ],
  },
  {
    id: 'up-next',
    name: 'Up Next',
    collapsed: false,
    tasks: [
      {
        id: 't8', name: 'Build Stripe webhook handler for recurring donations', assignee: TEAM_MEMBERS[3],
        status: 'Not Started', priority: 'Critical', size: 'XL', dueDate: '2026-03-01',
        tags: ['#api', '#backend'], subtasks: [
          { id: 't8-s1', name: 'Define webhook event schema', assignee: TEAM_MEMBERS[3], status: 'Not Started', priority: 'High', size: 'S', dueDate: '2026-02-25', tags: ['#api'] },
          { id: 't8-s2', name: 'Implement event processing queue', assignee: TEAM_MEMBERS[1], status: 'Not Started', priority: 'High', size: 'L', dueDate: '2026-02-28', tags: ['#backend'] },
          { id: 't8-s3', name: 'Add retry logic and error handling', assignee: TEAM_MEMBERS[3], status: 'Not Started', priority: 'Medium', size: 'M', dueDate: '2026-03-01', tags: ['#backend'] },
        ],
      },
      {
        id: 't9', name: 'Create admin dashboard for campaign performance metrics', assignee: TEAM_MEMBERS[4],
        status: 'Up Next', priority: 'High', size: 'L', dueDate: '2026-03-05',
        tags: ['#frontend', '#ux'], subtasks: [],
      },
      {
        id: 't10', name: 'Migrate legacy contact merge logic to Lightning', assignee: TEAM_MEMBERS[2],
        status: 'Not Started', priority: 'Medium', size: 'XL', dueDate: '2026-03-10',
        tags: ['#backend', '#database'], subtasks: [],
      },
      {
        id: 't11', name: 'Set up automated regression test suite', assignee: TEAM_MEMBERS[5],
        status: 'Up Next', priority: 'Medium', size: 'L', dueDate: '2026-03-08',
        tags: ['#testing', '#infrastructure'], subtasks: [],
      },
    ],
  },
  {
    id: 'backlog',
    name: 'Backlog',
    collapsed: true,
    tasks: [
      {
        id: 't12', name: 'Implement email template builder with merge fields', assignee: null,
        status: 'Not Started', priority: 'Low', size: 'XL', dueDate: null,
        tags: ['#frontend', '#ux'], subtasks: [],
      },
      {
        id: 't13', name: 'Add multi-currency support for international donations', assignee: null,
        status: 'Not Started', priority: 'Medium', size: 'XL', dueDate: null,
        tags: ['#backend', '#database'], subtasks: [],
      },
      {
        id: 't14', name: 'Document API endpoints for third-party integrations', assignee: TEAM_MEMBERS[1],
        status: 'Not Started', priority: 'Low', size: 'M', dueDate: null,
        tags: ['#documentation', '#api'], subtasks: [],
      },
      {
        id: 't15', name: 'Evaluate Lightning Web Components for record page refresh', assignee: null,
        status: 'Not Started', priority: 'Low', size: 'S', dueDate: null,
        tags: ['#frontend'], subtasks: [],
      },
      {
        id: 't16', name: 'Optimize database indexes for contact search', assignee: null,
        status: 'Not Started', priority: 'Medium', size: 'L', dueDate: null,
        tags: ['#performance', '#database'], subtasks: [],
      },
    ],
  },
];

// ============================================================
// Styles
// ============================================================

const styles = {
  container: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    backgroundColor: '#f3f3f3',
    minHeight: '100vh',
  },
  headerBar: {
    backgroundColor: 'white',
    borderBottom: '1px solid #dddbda',
    padding: '0 24px',
  },
  toolbar: {
    backgroundColor: 'white',
    border: '1px solid #dddbda',
    borderRadius: '4px',
    padding: '12px 20px',
    margin: '16px 24px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  summaryBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '12px 24px',
    flexWrap: 'wrap',
  },
  summaryChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: '600',
  },
  boardArea: {
    padding: '0 24px 24px',
  },
  groupContainer: {
    marginBottom: '20px',
    backgroundColor: 'white',
    borderRadius: '6px',
    border: '1px solid #dddbda',
    overflow: 'hidden',
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 16px',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background-color 0.1s',
  },
  groupName: {
    fontSize: '15px',
    fontWeight: '700',
    margin: 0,
  },
  groupCount: {
    fontSize: '12px',
    color: '#706e6b',
    fontWeight: '400',
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '36px 1fr 120px 150px 100px 70px 100px 160px',
    alignItems: 'center',
    padding: '8px 16px',
    backgroundColor: '#fafaf9',
    borderBottom: '1px solid #dddbda',
    fontSize: '11px',
    fontWeight: '700',
    color: '#706e6b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    gap: '4px',
  },
  taskRow: {
    display: 'grid',
    gridTemplateColumns: '36px 1fr 120px 150px 100px 70px 100px 160px',
    alignItems: 'center',
    padding: '0 16px',
    minHeight: '44px',
    borderBottom: '1px solid #f3f3f3',
    fontSize: '13px',
    color: '#080707',
    transition: 'background-color 0.1s',
    cursor: 'pointer',
    gap: '4px',
  },
  subtaskRow: {
    display: 'grid',
    gridTemplateColumns: '36px 1fr 120px 150px 100px 70px 100px 160px',
    alignItems: 'center',
    padding: '0 16px 0 48px',
    minHeight: '38px',
    borderBottom: '1px solid #f3f3f3',
    fontSize: '12px',
    color: '#3e3e3c',
    backgroundColor: '#fafaf9',
    cursor: 'pointer',
    gap: '4px',
  },
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3px 0',
    borderRadius: '2px',
    fontSize: '12px',
    fontWeight: '600',
    width: '100%',
    textAlign: 'center',
    lineHeight: '20px',
    minHeight: '26px',
  },
  priorityPill: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    padding: '3px 0',
    borderRadius: '2px',
    fontSize: '12px',
    fontWeight: '600',
    width: '100%',
    textAlign: 'center',
    lineHeight: '20px',
    minHeight: '26px',
  },
  sizePill: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '700',
    minWidth: '28px',
  },
  tagChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '1px 8px',
    borderRadius: '3px',
    fontSize: '11px',
    fontWeight: '500',
    backgroundColor: '#e1f0fd',
    color: '#0176d3',
    marginRight: '4px',
    marginBottom: '2px',
  },
  addItemRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    fontSize: '13px',
    color: '#706e6b',
    cursor: 'pointer',
    transition: 'background-color 0.1s, color 0.1s',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    borderRadius: '3px',
    border: '2px solid #dddbda',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
    flexShrink: 0,
  },
  checkboxChecked: {
    backgroundColor: '#0176d3',
    borderColor: '#0176d3',
  },
  progressBarTrack: {
    height: '6px',
    backgroundColor: '#dddbda',
    borderRadius: '3px',
    overflow: 'hidden',
    flex: 1,
  },
  progressBarFill: {
    height: '100%',
    borderRadius: '3px',
    transition: 'width 0.3s',
  },
  filterPillContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    padding: '8px 24px',
  },
  modalDetailGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
  },
  modalDetailField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  modalDetailLabel: {
    fontSize: '11px',
    color: '#706e6b',
    textTransform: 'uppercase',
    fontWeight: '600',
    letterSpacing: '0.5px',
  },
  modalDetailValue: {
    fontSize: '14px',
    color: '#080707',
    fontWeight: '500',
  },
};

// ============================================================
// Utility Helpers
// ============================================================

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr + 'T00:00:00');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const day = date.getDate();
  return `${month} ${day}`;
};

const isOverdue = (dateStr) => {
  if (!dateStr) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr + 'T00:00:00');
  return due < today;
};

const isDueSoon = (dateStr) => {
  if (!dateStr) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr + 'T00:00:00');
  const diff = (due - today) / (1000 * 60 * 60 * 24);
  return diff >= 0 && diff <= 2;
};

const getAllTasks = (groups) => {
  const tasks = [];
  groups.forEach((g) =>
    g.tasks.forEach((t) => {
      tasks.push(t);
      t.subtasks.forEach((s) => tasks.push(s));
    })
  );
  return tasks;
};

// ============================================================
// Sub-Components
// ============================================================

const StatusPill = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG['Not Started'];
  return (
    <span style={{ ...styles.statusPill, backgroundColor: config.bg, color: config.color }}>
      {config.label}
    </span>
  );
};

const PriorityPill = ({ priority }) => {
  const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG['Low'];
  return (
    <span style={{ ...styles.priorityPill, backgroundColor: config.bg, color: config.color }}>
      {config.icon && (
        <span style={{ fontSize: '11px', lineHeight: 1 }}>&#9888;</span>
      )}
      {priority}
    </span>
  );
};

const SizePill = ({ size }) => {
  if (!size) return <span style={{ color: '#706e6b' }}>—</span>;
  const config = SIZE_CONFIG[size] || SIZE_CONFIG['M'];
  return (
    <span style={{ ...styles.sizePill, backgroundColor: config.bg, color: config.color }}>
      {config.label}
    </span>
  );
};

const DueDateCell = ({ date, status }) => {
  if (!date) return <span style={{ color: '#706e6b', fontSize: '12px' }}>—</span>;
  const overdue = isOverdue(date) && status !== 'Done';
  const soon = !overdue && isDueSoon(date) && status !== 'Done';
  return (
    <span
      style={{
        fontSize: '12px',
        fontWeight: overdue || soon ? '600' : '400',
        color: overdue ? '#c23934' : soon ? '#c05c10' : '#3e3e3c',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
      }}
    >
      {overdue && <span style={{ fontSize: '10px' }}>&#9888;</span>}
      {formatDate(date)}
    </span>
  );
};

const TagsList = ({ tags }) => {
  if (!tags || tags.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
      {tags.map((tag) => (
        <span key={tag} style={styles.tagChip}>{tag}</span>
      ))}
    </div>
  );
};

const Checkbox = ({ checked, onChange, accentColor }) => (
  <div
    onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
    style={{
      ...styles.checkbox,
      ...(checked ? { ...styles.checkboxChecked, backgroundColor: accentColor || '#0176d3', borderColor: accentColor || '#0176d3' } : {}),
    }}
  >
    {checked && (
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )}
  </div>
);

const GroupProgressBar = ({ tasks }) => {
  const done = tasks.filter((t) => t.status === 'Done').length;
  const total = tasks.length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '120px' }}>
      <div style={styles.progressBarTrack}>
        <div
          style={{
            ...styles.progressBarFill,
            width: `${pct}%`,
            backgroundColor: pct === 100 ? '#2e844a' : '#0176d3',
          }}
        />
      </div>
      <span style={{ fontSize: '11px', color: '#706e6b', fontWeight: '600', whiteSpace: 'nowrap' }}>
        {done}/{total}
      </span>
    </div>
  );
};

// ============================================================
// Task Detail Modal
// ============================================================

const TaskDetailModal = ({ task, isOpen, onClose, groupName }) => {
  if (!task) return null;
  const statusConfig = STATUS_CONFIG[task.status] || STATUS_CONFIG['Not Started'];
  const priorityConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG['Low'];

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      heading={task.name}
      size="medium"
      footer={[
        <Button key="close" label="Close" onClick={onClose} />,
        <Button key="edit" label="Edit Task" variant="brand" onClick={onClose} />,
      ]}
    >
      <div style={{ padding: '20px' }}>
        {/* Group indicator */}
        <div style={{
          backgroundColor: '#eef4ff', border: '1px solid #d8edff', borderRadius: '4px',
          padding: '10px 16px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <Icon category="utility" name="task" size="x-small" style={{ fill: '#0176d3' }} />
          <span style={{ fontSize: '13px', color: '#0176d3', fontWeight: '600' }}>
            Group: {groupName}
          </span>
        </div>

        <div style={styles.modalDetailGrid}>
          <div style={styles.modalDetailField}>
            <span style={styles.modalDetailLabel}>Status</span>
            <div><StatusPill status={task.status} /></div>
          </div>
          <div style={styles.modalDetailField}>
            <span style={styles.modalDetailLabel}>Priority</span>
            <div><PriorityPill priority={task.priority} /></div>
          </div>
          <div style={styles.modalDetailField}>
            <span style={styles.modalDetailLabel}>Assignee</span>
            {task.assignee ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Avatar variant="user" label={task.assignee.name} size="small" title={task.assignee.name} />
                <span style={styles.modalDetailValue}>{task.assignee.name}</span>
              </div>
            ) : (
              <span style={{ color: '#706e6b', fontSize: '13px' }}>Unassigned</span>
            )}
          </div>
          <div style={styles.modalDetailField}>
            <span style={styles.modalDetailLabel}>Size</span>
            <div><SizePill size={task.size} /></div>
          </div>
          <div style={styles.modalDetailField}>
            <span style={styles.modalDetailLabel}>Due Date</span>
            <DueDateCell date={task.dueDate} status={task.status} />
          </div>
          <div style={styles.modalDetailField}>
            <span style={styles.modalDetailLabel}>Tags</span>
            <TagsList tags={task.tags} />
          </div>
        </div>

        {/* Subtasks */}
        {task.subtasks && task.subtasks.length > 0 && (
          <div style={{ marginTop: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', fontWeight: '700', color: '#080707' }}>
                Subtasks ({task.subtasks.length})
              </span>
              <GroupProgressBar tasks={task.subtasks} />
            </div>
            {task.subtasks.map((sub) => (
              <div key={sub.id} style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '8px 12px', borderBottom: '1px solid #f3f3f3',
              }}>
                <div style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  backgroundColor: (STATUS_CONFIG[sub.status] || STATUS_CONFIG['Not Started']).bg,
                  flexShrink: 0, border: sub.status === 'Not Started' ? '1px solid #dddbda' : 'none',
                }} />
                <span style={{
                  flex: 1, fontSize: '13px', color: '#080707',
                  textDecoration: sub.status === 'Done' ? 'line-through' : 'none',
                  opacity: sub.status === 'Done' ? 0.6 : 1,
                }}>
                  {sub.name}
                </span>
                {sub.assignee && (
                  <Avatar variant="user" label={sub.assignee.name} size="x-small" title={sub.assignee.name} />
                )}
                <DueDateCell date={sub.dueDate} status={sub.status} />
              </div>
            ))}
          </div>
        )}

        {/* Activity Placeholder */}
        <div style={{ marginTop: '24px' }}>
          <span style={{ fontSize: '14px', fontWeight: '700', color: '#080707', marginBottom: '12px', display: 'block' }}>
            Activity
          </span>
          <div style={{
            padding: '20px', textAlign: 'center', color: '#706e6b', fontSize: '13px',
            border: '1px dashed #dddbda', borderRadius: '4px',
          }}>
            Task activity and comments would appear here.
          </div>
        </div>
      </div>
    </Modal>
  );
};

// ============================================================
// New Task Modal
// ============================================================

const NewTaskModal = ({ isOpen, onClose, onAdd, groupId, groupName }) => {
  const [taskName, setTaskName] = useState('');

  const handleAdd = () => {
    if (taskName.trim()) {
      onAdd(groupId, taskName.trim());
      setTaskName('');
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => { setTaskName(''); onClose(); }}
      heading={`Add Task to ${groupName}`}
      size="small"
      footer={[
        <Button key="cancel" label="Cancel" onClick={() => { setTaskName(''); onClose(); }} />,
        <Button key="add" label="Add Task" variant="brand" onClick={handleAdd} disabled={!taskName.trim()} />,
      ]}
    >
      <div style={{ padding: '20px' }}>
        <Input
          label="Task Name"
          placeholder="Enter task name..."
          value={taskName}
          onChange={(e) => setTaskName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
        />
      </div>
    </Modal>
  );
};

// ============================================================
// Main Component
// ============================================================

const ProjectManagement = () => {
  const [groups, setGroups] = useState(() => generateTaskData());
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [expandedTasks, setExpandedTasks] = useState(new Set(['t1', 't8']));
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [priorityFilter, setPriorityFilter] = useState([]);
  const [hoveredRow, setHoveredRow] = useState(null);

  // Modal state
  const [detailTask, setDetailTask] = useState(null);
  const [detailGroupName, setDetailGroupName] = useState('');
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [newTaskGroupId, setNewTaskGroupId] = useState(null);
  const [newTaskGroupName, setNewTaskGroupName] = useState('');
  const [newTaskModalOpen, setNewTaskModalOpen] = useState(false);

  // Toast state
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((heading, variant = 'success') => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, heading, variant }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);

  // Filter options for combobox
  const statusOptions = useMemo(() =>
    Object.keys(STATUS_CONFIG).map((s) => ({ id: s, label: s })),
  []);
  const priorityOptions = useMemo(() =>
    Object.keys(PRIORITY_CONFIG).map((p) => ({ id: p, label: p })),
  []);

  // Filtered groups
  const filteredGroups = useMemo(() => {
    return groups.map((group) => ({
      ...group,
      tasks: group.tasks.filter((task) => {
        const matchesSearch = !searchTerm ||
          task.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (task.assignee && task.assignee.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
          task.tags.some((t) => t.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesStatus = statusFilter.length === 0 || statusFilter.some((f) => f.id === task.status);
        const matchesPriority = priorityFilter.length === 0 || priorityFilter.some((f) => f.id === task.priority);
        return matchesSearch && matchesStatus && matchesPriority;
      }),
    }));
  }, [groups, searchTerm, statusFilter, priorityFilter]);

  // Summary stats
  const stats = useMemo(() => {
    const allTasks = getAllTasks(groups);
    const statusCounts = {};
    allTasks.forEach((t) => {
      statusCounts[t.status] = (statusCounts[t.status] || 0) + 1;
    });
    return { total: allTasks.length, statusCounts };
  }, [groups]);

  // Active filters
  const activeFilters = useMemo(() => {
    const filters = [];
    statusFilter.forEach((f) => filters.push({ type: 'status', id: f.id, label: `Status: ${f.label}` }));
    priorityFilter.forEach((f) => filters.push({ type: 'priority', id: f.id, label: `Priority: ${f.label}` }));
    if (searchTerm.trim()) filters.push({ type: 'search', id: 'search', label: `Search: "${searchTerm}"` });
    return filters;
  }, [statusFilter, priorityFilter, searchTerm]);

  // Handlers
  const toggleGroup = useCallback((groupId) => {
    setGroups((prev) => prev.map((g) =>
      g.id === groupId ? { ...g, collapsed: !g.collapsed } : g
    ));
  }, []);

  const toggleTaskExpand = useCallback((taskId) => {
    setExpandedTasks((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  }, []);

  const toggleSelectTask = useCallback((taskId) => {
    setSelectedTasks((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  }, []);

  const handleTaskClick = useCallback((task, groupName) => {
    setDetailTask(task);
    setDetailGroupName(groupName);
    setDetailModalOpen(true);
  }, []);

  const handleAddTaskClick = useCallback((groupId, groupName) => {
    setNewTaskGroupId(groupId);
    setNewTaskGroupName(groupName);
    setNewTaskModalOpen(true);
  }, []);

  const handleAddTask = useCallback((groupId, taskName) => {
    const newTask = {
      id: `t-new-${Date.now()}`,
      name: taskName,
      assignee: null,
      status: 'Not Started',
      priority: 'Medium',
      size: null,
      dueDate: null,
      tags: [],
      subtasks: [],
    };
    setGroups((prev) => prev.map((g) =>
      g.id === groupId ? { ...g, tasks: [...g.tasks, newTask] } : g
    ));
    addToast(`Task "${taskName}" added`);
  }, [addToast]);

  const handleRemoveFilter = useCallback((filter) => {
    if (filter.type === 'status') setStatusFilter((prev) => prev.filter((f) => f.id !== filter.id));
    else if (filter.type === 'priority') setPriorityFilter((prev) => prev.filter((f) => f.id !== filter.id));
    else if (filter.type === 'search') setSearchTerm('');
  }, []);

  const handleClearFilters = useCallback(() => {
    setStatusFilter([]);
    setPriorityFilter([]);
    setSearchTerm('');
  }, []);

  const handleBulkStatusChange = useCallback((newStatus) => {
    setGroups((prev) => prev.map((g) => ({
      ...g,
      tasks: g.tasks.map((t) =>
        selectedTasks.has(t.id) ? { ...t, status: newStatus } : t
      ),
    })));
    addToast(`${selectedTasks.size} tasks updated to ${newStatus}`);
    setSelectedTasks(new Set());
  }, [selectedTasks, addToast]);

  // ============================================================
  // Render Helpers
  // ============================================================

  const renderToolbar = () => (
    <div style={styles.toolbar}>
      <div style={{ minWidth: '240px', flex: '0 1 280px' }}>
        <Input
          iconLeft={
            <InputIcon assistiveText={{ icon: 'Search' }} name="search" category="utility" />
          }
          placeholder="Search tasks, people, tags..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div style={{ minWidth: '160px', flex: '0 1 180px' }}>
        <Combobox
          labels={{ placeholder: 'Status' }}
          multiple
          options={statusOptions}
          selection={statusFilter}
          events={{
            onSelect: (e, data) => { if (data.selection) setStatusFilter(data.selection); },
            onRequestRemoveSelectedOption: (e, data) => setStatusFilter(data.selection),
          }}
          variant="readonly"
        />
      </div>
      <div style={{ minWidth: '140px', flex: '0 1 160px' }}>
        <Combobox
          labels={{ placeholder: 'Priority' }}
          multiple
          options={priorityOptions}
          selection={priorityFilter}
          events={{
            onSelect: (e, data) => { if (data.selection) setPriorityFilter(data.selection); },
            onRequestRemoveSelectedOption: (e, data) => setPriorityFilter(data.selection),
          }}
          variant="readonly"
        />
      </div>
      <div style={{ flex: 1 }} />
      <span style={{ fontSize: '12px', color: '#706e6b' }}>
        {filteredGroups.reduce((sum, g) => sum + g.tasks.length, 0)} tasks
      </span>
      <Button
        assistiveText={{ icon: 'Refresh' }}
        iconCategory="utility"
        iconName="refresh"
        variant="icon-border"
        title="Reset Data"
        onClick={() => {
          setGroups(generateTaskData());
          setSelectedTasks(new Set());
          setExpandedTasks(new Set(['t1', 't8']));
          addToast('Board data reset', 'info');
        }}
      />
    </div>
  );

  const renderActiveFilters = () => {
    if (activeFilters.length === 0) return null;
    return (
      <div style={styles.filterPillContainer}>
        <span style={{ fontSize: '12px', color: '#706e6b', fontWeight: '600' }}>Filters:</span>
        {activeFilters.map((filter) => (
          <Pill
            key={`${filter.type}-${filter.id}`}
            labels={{ label: filter.label }}
            onClick={() => handleRemoveFilter(filter)}
            onRemove={() => handleRemoveFilter(filter)}
          />
        ))}
        <Button label="Clear All" variant="base" onClick={handleClearFilters} style={{ fontSize: '12px', color: '#0176d3' }} />
      </div>
    );
  };

  const renderSummaryBar = () => {
    const topStatuses = ['Not Started', 'In Progress', 'Ready for Review', 'Done', 'Stuck', 'Blocked'];
    return (
      <div style={styles.summaryBar}>
        {topStatuses.map((status) => {
          const config = STATUS_CONFIG[status];
          const count = stats.statusCounts[status] || 0;
          if (count === 0) return null;
          return (
            <span
              key={status}
              style={{
                ...styles.summaryChip,
                backgroundColor: config.bg,
                color: config.color,
                border: status === 'Not Started' ? '1px solid #dddbda' : 'none',
              }}
            >
              <span style={{ fontWeight: '700' }}>{count}</span>
              {config.label}
            </span>
          );
        })}
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: '#706e6b' }}>Overall:</span>
          <GroupProgressBar tasks={getAllTasks(groups)} />
        </div>
      </div>
    );
  };

  const renderBulkActions = () => {
    if (selectedTasks.size === 0) return null;
    return (
      <div style={{
        backgroundColor: '#0176d3', color: 'white', padding: '8px 24px',
        display: 'flex', alignItems: 'center', gap: '12px',
        margin: '0 24px', borderRadius: '4px', marginBottom: '8px',
      }}>
        <Icon category="utility" name="check" size="x-small" style={{ fill: 'white' }} />
        <span style={{ fontWeight: '700', fontSize: '13px' }}>
          {selectedTasks.size} selected
        </span>
        <div style={{ flex: 1 }} />
        <Button
          label="Mark Done"
          variant="neutral"
          onClick={() => handleBulkStatusChange('Done')}
          style={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', fontSize: '12px' }}
        />
        <Button
          label="Mark In Progress"
          variant="neutral"
          onClick={() => handleBulkStatusChange('In Progress')}
          style={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', fontSize: '12px' }}
        />
        <Button
          label="Clear Selection"
          variant="neutral"
          onClick={() => setSelectedTasks(new Set())}
          style={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', fontSize: '12px' }}
        />
      </div>
    );
  };

  const renderTaskRow = (task, group, isSubtask = false) => {
    const rowStyle = isSubtask ? styles.subtaskRow : styles.taskRow;
    const isHovered = hoveredRow === task.id;
    const isSelected = selectedTasks.has(task.id);
    const hasSubtasks = task.subtasks && task.subtasks.length > 0;
    const isExpanded = expandedTasks.has(task.id);
    const groupColor = GROUP_COLORS[group.id] || '#706e6b';

    return (
      <React.Fragment key={task.id}>
        <div
          style={{
            ...rowStyle,
            backgroundColor: isSelected ? '#eef4ff' : isHovered ? '#fafaf9' : 'transparent',
            borderLeft: isSubtask ? 'none' : `3px solid ${groupColor}`,
            paddingLeft: isSubtask ? '48px' : '13px',
          }}
          onMouseEnter={() => setHoveredRow(task.id)}
          onMouseLeave={() => setHoveredRow(null)}
          onClick={() => handleTaskClick(task, group.name)}
        >
          {/* Checkbox */}
          <Checkbox
            checked={isSelected}
            onChange={() => toggleSelectTask(task.id)}
            accentColor={groupColor}
          />

          {/* Task Name */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden', minWidth: 0 }}>
            {hasSubtasks && (
              <span
                onClick={(e) => { e.stopPropagation(); toggleTaskExpand(task.id); }}
                style={{
                  cursor: 'pointer', fontSize: '10px', color: '#706e6b', flexShrink: 0,
                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.15s', display: 'inline-block', width: '14px', textAlign: 'center',
                }}
              >
                &#9654;
              </span>
            )}
            <span style={{
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              fontWeight: isSubtask ? '400' : '500',
              textDecoration: task.status === 'Done' ? 'line-through' : 'none',
              opacity: task.status === 'Done' ? 0.65 : 1,
            }}>
              {task.name}
            </span>
            {hasSubtasks && (
              <span style={{
                fontSize: '10px', color: '#706e6b', backgroundColor: '#f3f3f3',
                padding: '1px 6px', borderRadius: '8px', fontWeight: '600', flexShrink: 0,
              }}>
                {task.subtasks.length}
              </span>
            )}
          </div>

          {/* Assignee */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {task.assignee ? (
              <>
                <Avatar variant="user" label={task.assignee.name} size="x-small" title={task.assignee.name} />
                <span style={{ fontSize: '12px', color: '#3e3e3c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {task.assignee.name.split(' ')[0]}
                </span>
              </>
            ) : (
              <span style={{ fontSize: '12px', color: '#c9c7c5' }}>—</span>
            )}
          </div>

          {/* Status */}
          <StatusPill status={task.status} />

          {/* Priority */}
          <PriorityPill priority={task.priority} />

          {/* Size */}
          <div style={{ textAlign: 'center' }}>
            <SizePill size={task.size} />
          </div>

          {/* Due Date */}
          <DueDateCell date={task.dueDate} status={task.status} />

          {/* Tags */}
          <TagsList tags={task.tags} />
        </div>

        {/* Subtasks */}
        {hasSubtasks && isExpanded && task.subtasks.map((sub) =>
          renderTaskRow(sub, group, true)
        )}
      </React.Fragment>
    );
  };

  const renderGroup = (group) => {
    const color = GROUP_COLORS[group.id] || '#706e6b';
    const taskCount = group.tasks.length;

    return (
      <div key={group.id} style={styles.groupContainer}>
        {/* Group Header */}
        <div
          style={{
            ...styles.groupHeader,
            borderLeft: `4px solid ${color}`,
          }}
          onClick={() => toggleGroup(group.id)}
        >
          <span style={{
            fontSize: '10px', color: color,
            transform: group.collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
            transition: 'transform 0.15s', display: 'inline-block',
          }}>
            &#9654;
          </span>
          <h3 style={{ ...styles.groupName, color }}>{group.name}</h3>
          <span style={styles.groupCount}>{taskCount} {taskCount === 1 ? 'task' : 'tasks'}</span>
          <GroupProgressBar tasks={group.tasks} />
        </div>

        {/* Table */}
        {!group.collapsed && (
          <>
            {/* Column Headers */}
            <div style={{ ...styles.tableHeader, borderLeft: `4px solid ${color}` }}>
              <span></span>
              <span>Item</span>
              <span>Assignee</span>
              <span style={{ textAlign: 'center' }}>Status</span>
              <span style={{ textAlign: 'center' }}>Priority</span>
              <span style={{ textAlign: 'center' }}>Size</span>
              <span>Due Date</span>
              <span>Tags</span>
            </div>

            {/* Rows */}
            {group.tasks.map((task) => renderTaskRow(task, group))}

            {/* Add Item */}
            <div
              style={{
                ...styles.addItemRow,
                borderLeft: `4px solid ${color}`,
              }}
              onClick={() => handleAddTaskClick(group.id, group.name)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#fafaf9';
                e.currentTarget.style.color = color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#706e6b';
              }}
            >
              <Icon category="utility" name="add" size="xx-small" style={{ fill: 'currentColor' }} />
              <span>Add item</span>
            </div>
          </>
        )}
      </div>
    );
  };

  // ============================================================
  // Main Render
  // ============================================================

  return (
    <div style={styles.container}>
      {/* Toasts */}
      {toasts.length > 0 && (
        <ToastContainer>
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              labels={{ heading: toast.heading }}
              variant={toast.variant}
              duration={4000}
              onRequestClose={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
            />
          ))}
        </ToastContainer>
      )}

      {/* Page Header */}
      <div style={styles.headerBar}>
        <PageHeader
          icon={
            <Icon
              assistiveText={{ label: 'Project' }}
              category="standard"
              name="task"
              style={{ backgroundColor: '#0176d3', fill: 'white' }}
            />
          }
          label="Project"
          title="RevenuePoint Implementation"
          info="14 open tasks · 3 team members active"
          variant="record-home"
        />
      </div>

      {/* Toolbar */}
      {renderToolbar()}

      {/* Active Filters */}
      {renderActiveFilters()}

      {/* Summary Bar */}
      {renderSummaryBar()}

      {/* Bulk Actions */}
      {renderBulkActions()}

      {/* Board Groups */}
      <div style={styles.boardArea}>
        {filteredGroups.map((group) => renderGroup(group))}
      </div>

      {/* Detail Modal */}
      <TaskDetailModal
        task={detailTask}
        isOpen={detailModalOpen}
        onClose={() => { setDetailModalOpen(false); setDetailTask(null); }}
        groupName={detailGroupName}
      />

      {/* New Task Modal */}
      <NewTaskModal
        isOpen={newTaskModalOpen}
        onClose={() => setNewTaskModalOpen(false)}
        onAdd={handleAddTask}
        groupId={newTaskGroupId}
        groupName={newTaskGroupName}
      />
    </div>
  );
};

export default ProjectManagement;
